import { cn } from '@/lib/utils'
import { formatLabel } from '@/lib/utils/formatting'
import { TextClamp } from '../../text/TextClamp/TextClamp'
import { CHART_LIMITS } from '../../../foundations/slide-limits'
import { DECK_CHART_PRIMARY_COLOR } from '../../../foundations/chart-colors'

import { RankingChartViewItem } from './rankingChartViewItems'

/**
 * Safe division that prevents NaN/Infinity results.
 * Returns 0 if divisor is 0 or result is not finite.
 */
function safeDivide(numerator: number, divisor: number, fallback = 0): number {
  if (divisor === 0) return fallback
  const result = numerator / divisor
  return Number.isFinite(result) ? result : fallback
}

export interface RankingChartLabels {
  score: string
  higherScoreMorePreferred: string
  /** Shown in place of the bars when the payload carries no items. */
  empty: string
  formatScoreRange: (max: number) => string
  formatShowingTopItems: (shown: number, total: number) => string
  formatShowingTopRankedItems: (shown: number, total: number) => string
  formatSegmentBarTitle: (segment: string, score: string, count: number) => string
}

export const DEFAULT_RANKING_CHART_LABELS: RankingChartLabels = {
  score: 'score',
  higherScoreMorePreferred: 'Higher score = more preferred',
  empty: 'No ranking data available',
  formatScoreRange(max: number) {
    return `Score range: 1-${max}`
  },
  formatShowingTopItems(shown: number, total: number) {
    return `Showing top ${shown} of ${total} items.`
  },
  formatShowingTopRankedItems(shown: number, total: number) {
    return `Showing top ${shown} of ${total} ranked items.`
  },
  formatSegmentBarTitle(segment: string, score: string, count: number) {
    return `${segment}: score ${score} (${count} responses)`
  },
}

export interface RankingChartViewDefaultProps {
  variant?: 'default'
  items: Array<RankingChartViewItem>
  maxScore?: number
  colors?: Array<string>
  labels?: Partial<RankingChartLabels>
}

/**
 * One podium row (D13): rank badge, item tile or thumbnail, label, bar, value.
 *
 * `podium`, `per_item_rank` and `dim_rank` all draw through this one row
 * shape (`RankingPayload.items` in `contracts.py`) — the adapter maps the
 * payload's `RankingItem` onto these fields; this component draws rows, it
 * does not know about slots or payload shapes.
 */
export interface PodiumRankingItem {
  rank: number
  label: string
  tag?: string
  imageUrl?: string
  value: number
  /** The figure as the card should print it ("42%", "4.3 / 5"). Falls back to `value`. */
  valueLabel?: string
  /**
   * The way out of the ballot ("None of these"), from the payload's typed
   * `RankingItem.isEscape`. Not a ranked entrant: it is drawn after the
   * ranked rows as a plain label and share, with no rank badge and no tile,
   * and its `rank` is ignored.
   */
  isEscape?: boolean
}

export interface RankingChartPodiumLabels {
  /** Shown in place of the rows when the payload carries no items. */
  empty: string
  /** The tie badge ("Too close to call"). */
  tie: string
}

export const DEFAULT_RANKING_CHART_PODIUM_LABELS: RankingChartPodiumLabels = {
  empty: 'No ranking data available',
  tie: 'Too close to call',
}

export interface RankingChartViewPodiumProps {
  variant: 'podium'
  items: Array<PodiumRankingItem>
  /** No winner emphasis: the top two could not be separated. */
  isTie?: boolean
  /** What the figure means ("Preferred", "Opportunity"). */
  scoreLabel?: string
  /**
   * The value a full-width bar stands for. Defaults to the largest value in
   * `items`, which is right when these rows ARE the field. A caller drawing
   * only the TAIL of a ranking (the picture podium puts ranks 1-3 on a board
   * and the rest here) has to pass the whole field's maximum, or rank 4's bar
   * fills the track and reads as the leader.
   */
  maxValue?: number
  labels?: Partial<RankingChartPodiumLabels>
}

export type RankingChartViewProps = RankingChartViewDefaultProps | RankingChartViewPodiumProps

function podiumItemTile(item: PodiumRankingItem) {
  if (item.imageUrl) {
    return (
      <img
        src={item.imageUrl}
        alt={item.label}
        className="w-9 h-9 rounded-lg object-cover flex-shrink-0 border border-border/40"
      />
    )
  }
  const letter = item.tag ?? (item.label.trim().charAt(0).toUpperCase() || '?')
  return (
    <span
      data-testid={`ranking-podium-tile-${item.rank}`}
      className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center text-sm font-bold bg-muted/40 text-foreground/70"
    >
      {letter}
    </span>
  )
}

function RankingChartPodiumView(props: RankingChartViewPodiumProps) {
  const { items, isTie = false, scoreLabel, maxValue: maxValueProp, labels: labelsProp } = props
  const labels: RankingChartPodiumLabels = {
    ...DEFAULT_RANKING_CHART_PODIUM_LABELS,
    ...labelsProp,
  }

  if (items.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-muted-foreground min-h-48"
        data-testid="ranking-chart-view"
      >
        {labels.empty}
      </div>
    )
  }

  const maxValue = maxValueProp || Math.max(...items.map((item) => item.value), 0) || 1
  const ranked = items.filter((item) => !item.isEscape)
  const escapes = items.filter((item) => item.isEscape)

  return (
    <div className="w-full" data-testid="ranking-chart-view">
      {/*
        The tie badge is the reader's only warning that rank 1 is not a real
        win, so it hangs off `isTie` alone. `scoreLabel` is optional (a podium
        whose figure needs no gloss still ships one), and nesting the badge
        under it dropped the warning exactly where the chart was barest.
      */}
      {(scoreLabel || isTie) && (
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-sm font-medium text-muted-foreground">{scoreLabel ?? ''}</span>
          {isTie && (
            <span
              data-testid="ranking-podium-tie"
              className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-muted/60 text-foreground/70"
            >
              {labels.tie}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-col gap-4">
        {ranked.map((item) => {
          // Rank 1 is crowned only when this is a real, untied win (D13).
          const isWinner = item.rank === 1 && !isTie
          const barWidth = safeDivide(item.value, maxValue, 0) * 100
          const valueText = item.valueLabel ?? String(item.value)

          return (
            <div
              key={item.rank}
              data-testid={`ranking-podium-row-${item.rank}`}
              data-escape="false"
              className="flex flex-col gap-1.5"
            >
              <div className="flex items-center gap-3">
                <span
                  data-testid={`ranking-podium-rank-${item.rank}`}
                  className={cn(
                    'text-[11px] font-bold flex-shrink-0 tabular-nums whitespace-nowrap rounded-full px-2 py-0.5',
                    isWinner ? 'text-white' : 'text-muted-foreground',
                  )}
                  style={isWinner ? { backgroundColor: DECK_CHART_PRIMARY_COLOR } : undefined}
                >
                  #{item.rank}
                </span>
                {podiumItemTile(item)}
                <TextClamp
                  maxLines={2}
                  className="text-sm leading-snug flex-1 min-w-0 font-normal text-foreground"
                  title={formatLabel(item.label)}
                >
                  {formatLabel(item.label)}
                </TextClamp>
                <span className="text-sm font-normal text-foreground tabular-nums flex-shrink-0">
                  {valueText}
                </span>
              </div>
              <div className="w-full bg-muted/30 rounded-full overflow-hidden h-2">
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${barWidth}%`, backgroundColor: DECK_CHART_PRIMARY_COLOR }}
                />
              </div>
            </div>
          )
        })}
        {/*
          The escape options, after a rule and muted: a label and its share,
          no rank and no tile, because they were never in the race. Which rows
          these are comes from the typed `isEscape` flag alone.
        */}
        {escapes.length > 0 && (
          <div
            data-testid="ranking-podium-escapes"
            className={cn('flex flex-col gap-4 opacity-60', ranked.length > 0 && 'border-t border-border/50 pt-4')}
          >
            {escapes.map((item, index) => {
              const barWidth = safeDivide(item.value, maxValue, 0) * 100
              const valueText = item.valueLabel ?? String(item.value)

              return (
                <div
                  key={`escape-${index}`}
                  data-testid={`ranking-podium-escape-${index}`}
                  data-escape="true"
                  className="flex flex-col gap-1.5"
                >
                  <div className="flex items-center gap-3">
                    <TextClamp
                      maxLines={2}
                      className="text-sm leading-snug flex-1 min-w-0 font-normal text-foreground"
                      title={formatLabel(item.label)}
                    >
                      {formatLabel(item.label)}
                    </TextClamp>
                    <span className="text-sm font-normal text-foreground tabular-nums flex-shrink-0">
                      {valueText}
                    </span>
                  </div>
                  <div className="w-full bg-muted/30 rounded-full overflow-hidden h-2">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${barWidth}%`, backgroundColor: 'var(--slide-text-muted)' }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export function RankingChartView(props: RankingChartViewProps) {
  if (props.variant === 'podium') {
    return <RankingChartPodiumView {...props} />
  }

  const { items, maxScore: maxScoreProp, colors: colorsProp, labels: labelsProp } = props

  const labels: RankingChartLabels = {
    ...DEFAULT_RANKING_CHART_LABELS,
    ...labelsProp,
  }
  const effectiveBarColor = colorsProp?.[0] || DECK_CHART_PRIMARY_COLOR
  const maxScore = maxScoreProp ?? items.length
  const nonSegmentedMaxItems = CHART_LIMITS.horizontalBarMaxItems
  const displayedData = items.slice(0, nonSegmentedMaxItems)
  const hasMoreItems = items.length > nonSegmentedMaxItems

  if (items.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-muted-foreground min-h-48"
        data-testid="ranking-chart-view"
      >
        {labels.empty}
      </div>
    )
  }

  return (
    <div className="w-full" data-testid="ranking-chart-view">
      <div
        className="space-y-6 pt-2 overflow-hidden pr-4 max-h-96"
      >
        {displayedData.map((item) => {
          const barWidth = safeDivide(item.score, maxScore, 0) * 100

          return (
            <div key={item.rank} className="relative">
              <div className="flex items-center gap-3 mb-2">
                <TextClamp
                  maxLines={3}
                  className="text-foreground min-w-0 flex-1 leading-snug text-[14px] font-normal"
                  title={formatLabel(item.label)}
                >
                  {formatLabel(item.label)}
                </TextClamp>

                <div className="text-right shrink-0">
                  <span className="tabular-nums text-sm font-semibold">
                    {item.score.toFixed(1)}
                  </span>
                  <span className="ml-1 text-muted-foreground text-sm font-semibold">
                    {labels.score}
                  </span>
                </div>
              </div>
              
              <div className="w-full bg-muted/30 rounded-full overflow-hidden h-2">
                  <div 
                    className="h-full rounded-full transition-all duration-500 ease-out"
                    style={{ 
                      width: `${barWidth}%`,
                      backgroundColor: effectiveBarColor
                    }}
                  />
                </div>
            </div>
          )
        })}
      </div>
      {hasMoreItems && (
        <div className="text-center pt-3">
          <p className="text-sm text-muted-foreground italic">
            {labels.formatShowingTopRankedItems(nonSegmentedMaxItems, items.length)}
          </p>
        </div>
      )}
      
      <div className="mt-6 pt-4 border-t border-border/50">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--slide-success)' }} />
            <span>{labels.higherScoreMorePreferred}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs">{labels.formatScoreRange(maxScore)}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
